/**
 * src/game/systems/enemy-xp.ts — 不同敵人不同經驗(roguelite 單場 XP 倍率)。
 *
 * boss 2026-06-13:「不同敵人不同經驗」。把「擊殺一隻給多少單場 XP」做成資料 + 純函式,
 * 讓 scene 各擊殺呼叫點依該敵的 variant(basic/fast/tank)+ elite / mini-boss 傳對的倍率。
 *
 * 鐵則(學習與遊戲分離):這是 in-run XP(_runXp)倍率,純單場、升級觸發三選一,
 * **絕不**寫回 factionXP/freeXP/skillLevels/修煉值。倍率只影響「升級條漲多快」。
 */
import type { HEnemyVariant } from '../entities/h-enemy';

/**
 * 每種敵人 variant 的單場 XP 倍率(越硬/越快 → 給越多,獎勵打有難度的)。
 *   basic = 1(基準)、fast = 1.5(難打中,閃得快)、tank = 2(肉,耗時間)。
 */
export const VARIANT_XP_MULT: Record<HEnemyVariant, number> = {
  basic: 1,
  fast: 1.5,
  tank: 2,
};

/** 精英(有 affinity 光環的菁英雜兵)的單場 XP 倍率(蓋過 variant)。 */
export const ELITE_XP_MULT = 3;

/**
 * 算「擊殺這隻敵人」該給的單場 XP 倍率。優先序:mini-boss > elite > variant。
 *   - mini-boss:傳入 miniBossMult(scene 的 MINI_BOSS_XP_MULT,維持較高)。
 *   - elite(affinity 菁英):ELITE_XP_MULT。
 *   - 其餘:依 variant(basic/fast/tank)。
 * 純函式,可單測;不碰任何 progress/修煉值。
 */
export function enemyKillXpMult(
  params: {
    variant?: HEnemyVariant | null;
    elite?: boolean;
    miniBoss?: boolean;
  },
  miniBossMult: number,
): number {
  if (params.miniBoss) return miniBossMult;
  if (params.elite) return ELITE_XP_MULT;
  const v = params.variant ?? 'basic';
  return VARIANT_XP_MULT[v] ?? VARIANT_XP_MULT.basic;
}
